import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Box, Divider, Grid } from '@material-ui/core';
import moment from 'moment';
import { connect } from 'react-redux';
import styled from 'styled-components';
import {
  getAllStats,
  getAverageDeliveryDaysTop,
  getShippedQuantitiesTop
} from './dashboardActions';
import Bubble from './bubble/Bubble';
import truck from '../../img/truck.svg';
import shipmentTruck from '../../img/shipment.svg';
import returnTruck from '../../img/return.svg';
import delivery from '../../img/delivery.svg';
import StateChart from './chart/StateChart';
import ShippedPackageChart from './chart/ShippedPackageChart';
import ShippedItemsChart from './chart/ShippedItemsChart';
import ReturnReasonChart from './chart/ReturnReasonChart';
import ReceptionsChart from './chart/ReceptionsChart';
import ItemsChart from './chart/ItemsChart';
import ShippedAndReturnChart from './chart/ShippedAndReturnChart';
import AverageDeliveryDaysDialog from './chart/AverageDeliveryDaysDialog';
import PeriodicityButton from './chart/utils/PeriodicityButton';

const Title = styled.h1`
  font-size: 35px;
  color: #001c56;
  font-weight: bold;
  margin-bottom: 8px;
`;

const Title2 = styled.h2`
  font-size: 32px;
  color: #001c56;
  font-weight: 600;
  margin-bottom: 8px;
`;

/**
 * Composant principale englobant toute la partie dashboard
 */
const Dashboard = ({
  dispatchGetAllStats,
  shippedQuantities,
  averageDeliveryDaysTop,
  dispatchGetShippedQuantities,
  dispatchGetAverageDeliveryDays,
  loadingShippedQuantities,
  loadingAverageDeliveryDaysTop
}) => {
  const [
    averageDeliveryDaysDialogOpen,
    setAverageDeliveryDaysDialogOpen
  ] = useState(false);
  useEffect(() => {
    const startOfMonth = moment()
      .startOf('month')
      .add(3, 'hours')
      .toISOString();

    const dateTo = moment(new Date()).toISOString();

    const params = {
      shippedQuantitiesTopParams: {
        dateFrom: startOfMonth,
        dateTo,
        period: 'month'
      },
      reasonsQuantitiesParams: {
        dateFrom: startOfMonth,
        dateTo
      },
      multipleExchangesQuantityParams: {
        dateFrom: startOfMonth,
        dateTo
      },
      shippedItemsQuantitiesParams: {
        dateFrom: startOfMonth,
        dateTo
      },
      exchangesFollowedByRefundsQuantityParams: {
        dateFrom: startOfMonth,
        dateTo
      },
      averageDeliveryDaysParams: {
        dateFrom: startOfMonth,
        dateTo
      },
      averageDeliveryDaysTopParams: {
        dateFrom: startOfMonth,
        dateTo
      },
      quantitiesByStatesParams: {
        maximumShippingDate: moment(new Date()).toISOString()
      },
      shippedQuantitiesByCarrierParams: {
        dateFrom: startOfMonth,
        dateTo
      },
      processedShipmentsNaturesQuantitiesParams: {
        dateFrom: startOfMonth,
        dateTo,
        period: 'month'
      },
      processedItemsNaturesQuantitiesParams: {
        dateFrom: startOfMonth,
        dateTo,
        period: 'month'
      },
      receptionQuantitiesParams: {
        dateFrom: moment()
          .subtract(5, 'month')
          .startOf('month')
          .add(3, 'hours')
          .toISOString(),
        dateTo,
        period: 'month'
      },
      expectedProductsQuantitiesParams: {
        dateFrom: moment()
          .subtract(2, 'month')
          .startOf('month')
          .add(3, 'hours')
          .toISOString(),
        dateTo,
        period: 'month'
      },
      actualProductsQuantitiesParams: {
        dateFrom: moment()
          .subtract(2, 'month')
          .startOf('month')
          .add(3, 'hours')
          .toISOString(),
        dateTo,
        period: 'month'
      },
      processedQuantitiesParams: {
        dateFrom: startOfMonth,
        dateTo,
        period: 'month'
      }
    };
    dispatchGetAllStats(params);
  }, []);

  return (
    <div>
      <Title>Dashboard</Title>
      <AverageDeliveryDaysDialog
        open={averageDeliveryDaysDialogOpen}
        handleClose={() => setAverageDeliveryDaysDialogOpen(false)}
      />
      <Divider />
      <Box mb={4} />
      <Grid container>
        <Grid item md={5} xs={12}>
          <Bubble
            loading={loadingShippedQuantities}
            quantity={shippedQuantities.reduce(
              (a, b) => +a + +b.commandsQuantity,
              0
            )}
            title="Expéditions"
            subTitle="Sur la période (commande)"
            logo={truck}
          />
        </Grid>
        <Grid item md={5} xs={12}>
          <Bubble
            loading={loadingAverageDeliveryDaysTop}
            onClick={() => setAverageDeliveryDaysDialogOpen(true)}
            quantity={get(averageDeliveryDaysTop, 'globalAverage')}
            title="Livraison"
            subTitle="Temps moyen (jours) "
            logo={delivery}
          />
        </Grid>
        <Grid item md={2} xs={12}>
          <PeriodicityButton
            onChange={({ dateFrom, dateTo }) => {
              dispatchGetAverageDeliveryDays({
                dateFrom,
                dateTo,
                period: 'month'
              });
              dispatchGetShippedQuantities({
                dateFrom,
                dateTo,
                period: 'month'
              });
            }}
          />
        </Grid>
      </Grid>
      <Box mb={6} />
      <StateChart />
      <Box mb={6} mt={6} display="flex">
        <img src={shipmentTruck} alt="return truck" />
        <Box ml={3} />
        <Title2>Expéditions</Title2>
      </Box>
      <Grid container spacing={4}>
        <Grid item md={6}>
          <ShippedPackageChart />
        </Grid>
        <Grid item md={6}>
          <ShippedItemsChart />
        </Grid>
      </Grid>
      <Box mb={6} mt={6} display="flex">
        <img src={returnTruck} alt="return truck" />
        <Box ml={3} />
        <Title2>Retours</Title2>
      </Box>
      <Grid container spacing={4}>
        <Grid item md={6}>
          <ShippedAndReturnChart />
        </Grid>
        <Grid item md={6}>
          <ReturnReasonChart />
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item md={6}>
          <ReceptionsChart />
        </Grid>
        <Grid item md={6}>
          <ItemsChart />
        </Grid>
      </Grid>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  dispatchGetAllStats: params => dispatch(getAllStats(params)),
  dispatchGetShippedQuantities: params =>
    dispatch(getShippedQuantitiesTop(params)),
  dispatchGetAverageDeliveryDays: params =>
    dispatch(getAverageDeliveryDaysTop(params))
});

const mapStateToProps = state => ({
  shippedQuantities: state.dashboard.shippedQuantitiesTop,
  averageDeliveryDaysTop: state.dashboard.averageDeliveryDaysTop,
  loadingShippedQuantities: state.dashboard.loadingShippedQuantitiesTop,
  loadingAverageDeliveryDaysTop: state.dashboard.loadingAverageDeliveryDaysTop
});

Dashboard.propTypes = {
  dispatchGetAllStats: PropTypes.func,
  dispatchGetShippedQuantities: PropTypes.func,
  dispatchGetAverageDeliveryDays: PropTypes.func,
  shippedQuantities: PropTypes.array,
  averageDeliveryDaysTop: PropTypes.object,
  loadingShippedQuantities: PropTypes.bool,
  loadingAverageDeliveryDaysTop: PropTypes.bool
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
